// @ts-strict-ignore
/**
 * @fileoverview a collection of time utils
 */

/**
 * @deprecated TODO: remove moment and replace with new date/time utils. For details, see https://confluence.braze.com/display/ENG/Date+and+time+handling
 */
import moment from "moment";

import { TimeUnit } from "./types";
import type { TimeUnitKey, ISODateString, ISOTimeString, ISODateTimeString } from "./types";

export const DESCENDING_UNITS: TimeUnitKey[] = ["Week", "Day", "Hour", "Minute", "Second"];

/**
 * Returns the value of the greatest whole-number time unit for the given time value
 * e.g. 360 will get converted to [6, "Minute"]
 *
 * @deprecated by `@services/getGreatestWholeTimeUnit`
 *
 * @param value time value (in seconds if no unit is given)
 * @param timeUnit the optional time unit of the value
 */
export const getGreatestWholeTimeUnit = (
  value: number,
  timeUnit: TimeUnitKey = "Second",
  options = {
    availableTimeUnits: DESCENDING_UNITS,
  }
): [scalar: number, unit: TimeUnitKey] => {
  if (value === 0) {
    return [0, "Second"];
  }
  const basisSeconds = TimeUnit[timeUnit] * value;
  const gcm = DESCENDING_UNITS.filter((unit) => options?.availableTimeUnits.includes(unit)).find((unit) => {
    return basisSeconds % TimeUnit[unit] === 0;
  });

  if (gcm) {
    return [basisSeconds / TimeUnit[gcm], gcm];
  }

  return [basisSeconds, "Second"];
};

//#region typings and utilities for time strings

/** matches: [1] hours, [2] minutes */
const basicISOTimeRegex = /^([0-9]{2}):([0-9]{2})$/;

/** type assertion to assert that the given string is valid ISO time */
export const isISOTimeString = (value: string): value is ISOTimeString => {
  try {
    const [, hour, minute] = value.match(basicISOTimeRegex);

    return parseInt(hour) < 24 && parseInt(minute) < 60;
  } catch (e) {
    return false;
  }
};

/**
 * matches [1] year [2] month [3] day
 */
const basicISODateRegex = /^([0-9]{4})-([0-1][0-9])-([0-9]{2})$/;

export const isISODateString = (value: string): value is ISODateString => {
  try {
    let [, year, month, day]: any[] = value.match(basicISODateRegex);

    year = parseInt(year);
    month = parseInt(month);
    day = parseInt(day);

    return year >= 0 && month <= 12 && month > 0 && day <= 31 && day > 0;
  } catch (e) {
    return false;
  }
};

/**
 * The value as an `ISOTimeString`, for a string whose format is guaranteed by whatever produced
 * it (a schema, a serializer) but typed as plain `string`. Throws when the guarantee does not hold,
 * so a bad value fails where it was made rather than where it is used.
 */
export const asISOTimeString = (value: string): ISOTimeString => {
  if (!isISOTimeString(value)) {
    throw new TypeError(`"${value}" is not an HH:mm time string`);
  }
  return value;
};

/** as `asISOTimeString`, for an `ISODateString` */
export const asISODateString = (value: string): ISODateString => {
  if (!isISODateString(value)) {
    throw new TypeError(`"${value}" is not a YYYY-MM-DD date string`);
  }
  return value;
};

/**
 * matches [1] date [2] year [3] month [4] day [5] time [6] hours [7] minutes
 */
const basicISODateTimeRegex = /^(([0-9]{4})-([0-1][0-9])-([0-9]{2}))T(([0-9]{2}):([0-9]{2}))/;

/**
 * @deprecated
 */
export const toISODateTimeString = (date: Date) => {
  return moment(date).format("YYYY-MM-DDTHH:mm");
};

export const isISODateTimeString = (value: string): value is ISODateTimeString => {
  try {
    const [, date, , , , time] = value.match(basicISODateTimeRegex);

    return isISODateString(date) && isISOTimeString(time);
  } catch (e) {
    return false;
  }
};

/**
 * @deprecated by `import { convertSecondsToTime } from @services/convertDatetimeToRecipientsLocalTimestamp`
 */
export const parseSeconds = (seconds: number): ISOTimeString => {
  const [hours, minutes] = [Math.floor(seconds / TimeUnit.Hour) % 24, Math.floor(seconds / TimeUnit.Minute) % 60];

  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}` as ISOTimeString;
};

/**
 * @deprecated by `import { convertTimeToSeconds } from @services/convertDatetimeToRecipientsLocalTimestamp`
 */
export const toSeconds = (time: ISOTimeString): number => {
  const [hours, minutes] = time.split(":");
  return parseInt(hours) * TimeUnit.Hour + parseInt(minutes) * TimeUnit.Minute;
};

/**
 * @depracted by `import { convertDatetimeToRecipientsLocalTimestamp } from @services/convertDatetimeToRecipientsLocalTimestamp`
 */
export const toUTC = (date: ISODateString, time: ISOTimeString): number => {
  const [year, month, day] = date.split("-");
  const utc = Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day));

  return utc + toSeconds(time) * 1000;
};

//#endregion
